/**
 * Attempts to complete a truncated JSON string by appending the minimum
 * closing tokens needed to make it parseable.
 *
 * Walks the string tracking open delimiters and string state, then closes
 * everything in reverse order at the truncation point.
 *
 * @param {string} raw - The potentially incomplete JSON string
 * @returns {string | null} A completed JSON string, or null if the input
 *   doesn't look like JSON (doesn't start with { or [)
 */
export function completePartialJson(raw) {
  if (!raw || typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;

  const firstChar = trimmed[0];
  if (firstChar !== '{' && firstChar !== '[') return null;

  const stack = [];
  let inString = false;
  let escaped = false;
  let lastSignificantChar = '';

  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (ch === '\\' && inString) {
      escaped = true;
      continue;
    }

    if (ch === '"') {
      if (inString) {
        inString = false;
        lastSignificantChar = '"';
      } else {
        inString = true;
      }
      continue;
    }

    if (inString) continue;

    if (ch === '{' || ch === '[') {
      stack.push(ch);
      lastSignificantChar = ch;
    } else if (ch === '}') {
      if (stack.length > 0 && stack[stack.length - 1] === '{') {
        stack.pop();
      }
      lastSignificantChar = '}';
    } else if (ch === ']') {
      if (stack.length > 0 && stack[stack.length - 1] === '[') {
        stack.pop();
      }
      lastSignificantChar = ']';
    } else if (ch === ':' || ch === ',') {
      lastSignificantChar = ch;
    }
  }

  if (stack.length === 0 && !inString) {
    return trimmed;
  }

  let suffix = '';

  if (inString) {
    suffix += '"';
  }

  if (lastSignificantChar === ':' || lastSignificantChar === ',') {
    const topOfStack = stack.length > 0 ? stack[stack.length - 1] : null;
    if (lastSignificantChar === ':') {
      suffix += 'null';
    } else if (lastSignificantChar === ',' && topOfStack === '{') {
      // Trailing comma inside an object -- remove it and close
      const lastComma = trimmed.lastIndexOf(',');
      const base = trimmed.substring(0, lastComma);
      suffix = '';
      if (inString) suffix += '"';
      for (let j = stack.length - 1; j >= 0; j--) {
        suffix += stack[j] === '{' ? '}' : ']';
      }
      return base + suffix;
    }
  }

  for (let j = stack.length - 1; j >= 0; j--) {
    suffix += stack[j] === '{' ? '}' : ']';
  }

  return trimmed + suffix;
}
